<script lang="ts">
  import { onMount, onDestroy } from "svelte"
  import type { Chart as ChartInstance } from "chart.js"

  type RiskEventData = {
    labels: string[]
    pii_values: number[]
    secret_values: number[]
    editor_values?: number[]
    details?: Record<string, RiskEventDetail[]>
  }

  type RiskEventDetail = {
    id: number
    detected_type: string
    detector_source: string
    action: string
    severity: string
    user: string
    client_name: string
    task_type: string
    project_code: string
    sample_masked_value: string
    detail_path: string
  }

  let { data = { labels: [], pii_values: [], secret_values: [], editor_values: [], details: {} } }: { data: RiskEventData } = $props()

  let canvas: HTMLCanvasElement | undefined = $state()
  let chart: ChartInstance | null = null
  let selectedLabel: string | null = $state(null)

  type ChartTheme = {
    accent: string
    border: string
    success: string
    textSecondary: string
  }

  const fallbackTheme: ChartTheme = {
    accent: "#3E6AE1",
    border: "#E3E5E8",
    success: "#2EAD5B",
    textSecondary: "#5F6368"
  }

  const themeColor = (name: string, fallback: string) => {
    const css = getComputedStyle(document.documentElement)
    return css.getPropertyValue(name).trim() || fallback
  }

  const resolveChartTheme = (): ChartTheme => ({
    accent: themeColor("--accent", fallbackTheme.accent),
    border: themeColor("--border", fallbackTheme.border),
    success: themeColor("--success", fallbackTheme.success),
    textSecondary: themeColor("--text-secondary", fallbackTheme.textSecondary)
  })

  let chartTheme = $state<ChartTheme>(fallbackTheme)
  let themeObserver: MutationObserver | null = null

  const editorValues = $derived(data.editor_values ?? [])
  const selectedEvents = $derived(selectedLabel ? (data.details?.[selectedLabel] ?? []) : [])
  const hasData = $derived(
    data.pii_values.some((value) => value > 0) ||
      data.secret_values.some((value) => value > 0) ||
      editorValues.some((value) => value > 0)
  )

  const applyChartTheme = () => {
    const nextTheme = resolveChartTheme()
    chartTheme = nextTheme

    if (!chart) return

    const [piiDataset, secretDataset, editorDataset] = chart.data.datasets
    if (piiDataset) {
      piiDataset.borderColor = nextTheme.accent
      piiDataset.backgroundColor = nextTheme.accent
    }
    if (secretDataset) {
      secretDataset.borderColor = nextTheme.border
      secretDataset.backgroundColor = nextTheme.border
    }
    if (editorDataset) {
      editorDataset.borderColor = nextTheme.success
      editorDataset.backgroundColor = nextTheme.success
    }

    const legendLabels = chart.options.plugins?.legend?.labels
    if (legendLabels) legendLabels.color = nextTheme.textSecondary

    const xTicks = chart.options.scales?.x?.ticks
    if (xTicks) xTicks.color = nextTheme.textSecondary

    const yTicks = chart.options.scales?.y?.ticks
    if (yTicks) yTicks.color = nextTheme.textSecondary

    const yGrid = chart.options.scales?.y?.grid
    if (yGrid) yGrid.color = nextTheme.border

    chart.update()
  }

  onMount(async () => {
    if (!canvas) return

    chartTheme = resolveChartTheme()

    const { Chart, registerables } = await import("chart.js")
    Chart.register(...registerables)

    chart = new Chart(canvas, {
      type: "line",
      data: {
        labels: data.labels,
        datasets: [
          {
            label: "PII 탐지",
            data: data.pii_values,
            borderColor: chartTheme.accent,
            backgroundColor: chartTheme.accent,
            pointRadius: 2,
            pointHoverRadius: 4,
            tension: 0.3
          },
          {
            label: "Secret 탐지",
            data: data.secret_values,
            borderColor: chartTheme.border,
            backgroundColor: chartTheme.border,
            pointRadius: 2,
            pointHoverRadius: 4,
            tension: 0.3
          },
          {
            label: "에디터 탐지",
            data: editorValues,
            borderColor: chartTheme.success,
            backgroundColor: chartTheme.success,
            pointRadius: 2,
            pointHoverRadius: 4,
            tension: 0.3
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          intersect: false,
          mode: "index"
        },
        plugins: {
          legend: {
            display: true,
            labels: {
            color: chartTheme.textSecondary,
              boxWidth: 10,
              boxHeight: 10,
              font: { size: 12 }
            }
          }
        },
        onClick: (_event, elements) => {
          const element = elements[0]
          if (!element) return

          selectedLabel = data.labels[element.index] ?? null
        },
        scales: {
          x: {
            ticks: { color: chartTheme.textSecondary, font: { size: 11 }, maxTicksLimit: 8 },
            grid: { display: false }
          },
          y: {
            beginAtZero: true,
            ticks: { color: chartTheme.textSecondary, font: { size: 11 }, precision: 0 },
            grid: { color: chartTheme.border }
          }
        }
      }
    })

    themeObserver = new MutationObserver(applyChartTheme)
    themeObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"]
    })
  })

  onDestroy(() => {
    themeObserver?.disconnect()
    chart?.destroy()
    themeObserver = null
    chart = null
  })
</script>

<div class="relative" style="height: 220px;">
  {#if hasData}
    <canvas bind:this={canvas} aria-label="PII, Secret, 에디터 탐지 추이 차트"></canvas>
  {:else}
    <div class="flex h-full items-center justify-center gate-panel gate-muted">
      아직 표시할 위험 이벤트가 없습니다.
    </div>
  {/if}
</div>

{#if hasData}
  <!-- 디자인 시스템 준수: 차트 하단에 접근성을 고려한 구조적 텍스트 요약 및 Tabular Numbers 제공 -->
  <div class="mt-4 pt-3 border-t border-[var(--border)]" role="region" aria-label="위험 탐지 데이터 요약">
    <dl class="grid grid-cols-3 gap-4 text-center">
      <div class="flex flex-col items-center">
        <dt class="text-[11px] font-bold text-[var(--text-secondary)] uppercase tracking-wider">PII 개인정보</dt>
        <dd class="text-base font-bold text-[var(--accent)] mt-1 tabular-nums" style="font-variant-numeric: tabular-nums;">
          {data.pii_values.reduce((s, v) => s + v, 0).toLocaleString()} <span class="text-xs font-normal text-[var(--text-secondary)]">건</span>
        </dd>
      </div>
      <div class="flex flex-col items-center border-x border-[var(--border)] px-2">
        <dt class="text-[11px] font-bold text-[var(--text-secondary)] uppercase tracking-wider">Secret / API 키</dt>
        <dd class="text-base font-bold text-[var(--text-secondary)] mt-1 tabular-nums" style="font-variant-numeric: tabular-nums;">
          {data.secret_values.reduce((s, v) => s + v, 0).toLocaleString()} <span class="text-xs font-normal text-[var(--text-secondary)]">건</span>
        </dd>
      </div>
      <div class="flex flex-col items-center">
        <dt class="text-[11px] font-bold text-[var(--text-secondary)] uppercase tracking-wider">코드 에디터</dt>
        <dd class="text-base font-bold text-[var(--success)] mt-1 tabular-nums" style="font-variant-numeric: tabular-nums;">
          {editorValues.reduce((s, v) => s + v, 0).toLocaleString()} <span class="text-xs font-normal text-[var(--text-secondary)]">건</span>
        </dd>
      </div>
    </dl>
  </div>
{/if}

{#if selectedLabel}
  <div class="chart-detail-backdrop" role="presentation" onclick={() => (selectedLabel = null)}>
    <div class="chart-detail-dialog" role="dialog" aria-modal="true" aria-labelledby="chart-detail-title" tabindex="-1" onclick={(event) => event.stopPropagation()} onkeydown={(event) => event.key === "Escape" && (selectedLabel = null)}>
      <header class="chart-detail-header">
        <div>
          <p>Detection detail</p>
          <h3 id="chart-detail-title">{selectedLabel} 탐지 상세</h3>
        </div>
        <button type="button" class="chart-detail-close" aria-label="상세 팝업 닫기" onclick={() => (selectedLabel = null)}>닫기</button>
      </header>

      {#if selectedEvents.length > 0}
        <div class="chart-detail-list">
          {#each selectedEvents as event (event.id)}
            <article class="chart-detail-item">
              <div>
                <strong>{event.detected_type}</strong>
                <span>{event.action} · {event.severity}</span>
              </div>
              <dl>
                <div><dt>사용자</dt><dd>{event.user}</dd></div>
                <div><dt>에디터</dt><dd>{event.client_name}</dd></div>
                <div><dt>업무</dt><dd>{event.task_type}</dd></div>
                <div><dt>프로젝트</dt><dd>{event.project_code}</dd></div>
                <div><dt>탐지기</dt><dd>{event.detector_source}</dd></div>
                <div><dt>샘플</dt><dd>{event.sample_masked_value}</dd></div>
              </dl>
              <a href={event.detail_path}>전체 상세 보기</a>
            </article>
          {/each}
        </div>
      {:else}
        <div class="chart-detail-empty">이 날짜의 상세 이벤트가 없습니다.</div>
      {/if}
    </div>
  </div>
{/if}

<style>
  .chart-detail-backdrop {
    position: fixed;
    inset: 0;
    z-index: 90;
    display: grid;
    place-items: center;
    background: rgba(23, 26, 32, 0.36);
    padding: 24px;
  }

  .chart-detail-dialog {
    width: min(720px, 100%);
    max-height: min(720px, 86dvh);
    overflow: auto;
    border: 1px solid var(--border);
    border-radius: 8px;
    background: var(--surface)fff;
    box-shadow: 0 24px 80px rgba(23, 26, 32, 0.22);
  }

  .chart-detail-header {
    position: sticky;
    top: 0;
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    border-bottom: 1px solid var(--border-soft);
    background: var(--surface)fff;
    padding: 20px 22px;
  }

  .chart-detail-header p {
    margin: 0 0 4px;
    color: var(--text-secondary);
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  .chart-detail-header h3 {
    margin: 0;
    color: var(--text-strong);
    font-size: 20px;
    font-weight: 800;
    letter-spacing: -0.02em;
  }

  .chart-detail-close {
    min-height: 36px;
    border: 1px solid var(--border);
    border-radius: 6px;
    background: var(--surface)fff;
    color: var(--text-strong);
    padding: 0 14px;
    font-size: 13px;
    font-weight: 650;
    cursor: pointer;
  }

  .chart-detail-list {
    display: grid;
    gap: 12px;
    padding: 18px;
  }

  .chart-detail-item {
    display: grid;
    gap: 14px;
    border: 1px solid var(--border-soft);
    border-radius: 8px;
    background: #f8f9fb;
    padding: 16px;
  }

  .chart-detail-item > div:first-child {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
  }

  .chart-detail-item strong {
    color: var(--text-strong);
    font-size: 15px;
  }

  .chart-detail-item span,
  .chart-detail-item dt {
    color: var(--text-secondary);
    font-size: 12px;
  }

  .chart-detail-item dl {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px 14px;
    margin: 0;
  }

  .chart-detail-item dl div {
    display: grid;
    gap: 3px;
  }

  .chart-detail-item dd {
    margin: 0;
    color: var(--text-strong);
    font-size: 13px;
    overflow-wrap: anywhere;
  }

  .chart-detail-item a {
    color: var(--text-strong);
    font-size: 13px;
    font-weight: 700;
    text-decoration: none;
  }

  .chart-detail-item a:hover {
    text-decoration: underline;
    text-underline-offset: 3px;
  }

  .chart-detail-empty {
    padding: 36px 20px;
    color: var(--text-secondary);
    text-align: center;
  }

  @media (max-width: 640px) {
    .chart-detail-item dl {
      grid-template-columns: 1fr;
    }
  }
</style>
